import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../supabase';
import SchoolSidebar from './SchoolSidebar';
import { SchoolMobileHeader } from './SchoolMobileHeader';
import BranchSelector from './BranchSelector';
import { useBranchFilter } from '../../hooks/useBranchFilter';
import './SchoolDashboard.css';
import './StudentFees.css';

function StudentFees() {
  const navigate = useNavigate();
  const [schoolData, setSchoolData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const handleLogout = () => navigate('/schoollogin');
  const { selectedBranchId, handleBranchChange } = useBranchFilter();
  const [classes, setClasses] = useState([]);
  const [sections, setSections] = useState([]);
  const [students, setStudents] = useState([]);
  const [selectedClassId, setSelectedClassId] = useState('');
  const [selectedSectionId, setSelectedSectionId] = useState('');
  const [loadingStudents, setLoadingStudents] = useState(false);
  const [loadingSections, setLoadingSections] = useState(false);
  const [studentsFees, setStudentsFees] = useState({}); // {studentId: [fees]}
  const [loadingFees, setLoadingFees] = useState(false);

  useEffect(() => {
    const storedSchoolData = localStorage.getItem('schoolData');
    if (storedSchoolData) {
      setSchoolData(JSON.parse(storedSchoolData));
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    if (schoolData?.id) {
      fetchClasses();
    }
  }, [schoolData?.id, selectedBranchId]);

  useEffect(() => {
    setSelectedClassId('');
    setSelectedSectionId('');
    setStudents([]);
    setSections([]);
    setStudentsFees({});
  }, [selectedBranchId]);

  useEffect(() => {
    if (schoolData?.id && selectedClassId) {
      fetchSections();
    } else {
      setSections([]);
    }
  }, [schoolData?.id, selectedClassId]);

  useEffect(() => {
    if (schoolData?.id && selectedClassId && selectedSectionId) {
      fetchStudents();
    } else {
      setStudents([]);
      setStudentsFees({});
    }
  }, [schoolData?.id, selectedClassId, selectedSectionId]);

  useEffect(() => {
    if (students.length > 0) {
      fetchStudentsFees();
    }
  }, [students]);

  const fetchClasses = async () => {
    try {
      let query = supabase
        .from('classes')
        .select('*')
        .eq('school_id', schoolData.id)
        .eq('is_active', true);

      if (selectedBranchId != null) {
        query = query.eq('branch_id', selectedBranchId);
      }

      const { data, error } = await query.order('class_name', { ascending: true });

      if (error) {
        console.error('Error fetching classes:', error);
        return;
      }

      setClasses(data || []);
    } catch (err) {
      console.error('Error:', err);
    }
  };

  const fetchSections = async () => {
    if (!selectedClassId) {
      setSections([]);
      setLoadingSections(false);
      return;
    }
    
    setLoadingSections(true);
    try {
      const { data: classData, error: classError } = await supabase
        .from('classes')
        .select('sections')
        .eq('id', selectedClassId)
        .single();
      
      if (classError) {
        let fallbackQuery = supabase
          .from('students')
          .select('section_id')
          .eq('school_id', schoolData.id)
          .eq('class_id', selectedClassId)
          .eq('is_active', true);

        if (selectedBranchId != null) {
          fallbackQuery = fallbackQuery.eq('branch_id', selectedBranchId);
        }

        const { data: studentsData } = await fallbackQuery;

        if (studentsData) {
          const uniqueSections = [...new Set(studentsData.map(s => s.section_id).filter(Boolean))];
          setSections(uniqueSections.map(s => ({ section_name: s })));
        } else {
          setSections([]);
        }
      } else {
        if (classData?.sections && Array.isArray(classData.sections)) {
          setSections(classData.sections.map(s => ({ section_name: s })));
        } else {
          setSections([]);
        }
      }
    } catch (err) {
      console.error('Error:', err);
    } finally {
      setLoadingSections(false);
    }
  };

  const fetchStudents = async () => {
    setLoadingStudents(true);
    try {
      let query = supabase
        .from('students')
        .select('*')
        .eq('school_id', schoolData.id)
        .eq('class_id', selectedClassId)
        .eq('is_active', true)
        .order('first_name', { ascending: true });

      if (selectedSectionId) {
        query = query.eq('section_id', selectedSectionId);
      }

      if (selectedBranchId != null) {
        query = query.eq('branch_id', selectedBranchId);
      }

      const { data, error } = await query;

      if (error) {
        console.error('Error fetching students:', error);
        return;
      }

      setStudents(data || []);
    } catch (err) {
      console.error('Error:', err);
    } finally {
      setLoadingStudents(false);
    }
  };

  const fetchStudentsFees = async () => {
    if (students.length === 0) return;
    
    setLoadingFees(true);
    try {
      const feesByStudent = {};
      
      for (const student of students) {
        if (!student.school_id || !student.class_id) continue;
        
        try {
          // Get today's date
          const today = new Date();
          const todayStr = today.toISOString().split('T')[0];
          
          // Fetch class fees
          let classFeesQuery = supabase
            .from('class_fees')
            .select('*')
            .eq('school_id', student.school_id)
            .eq('class_id', student.class_id)
            .eq('is_active', true);
          if (selectedBranchId != null) {
            classFeesQuery = classFeesQuery.eq('branch_id', selectedBranchId);
          }
          const { data: classFeesData, error: classFeesError } = await classFeesQuery.single();

          if (classFeesError && classFeesError.code !== 'PGRST116') {
            continue;
          }

          if (!classFeesData) continue;

          // Fetch fee schedule
          let feeScheduleQuery = supabase
            .from('fee_schedule')
            .select('*')
            .eq('school_id', student.school_id)
            .eq('class_id', student.class_id)
            .eq('is_active', true)
            .order('fee_date', { ascending: true });
          if (selectedBranchId != null) {
            feeScheduleQuery = feeScheduleQuery.eq('branch_id', selectedBranchId);
          }
          const { data: feeScheduleData } = await feeScheduleQuery;

          // Fetch additional fees
          let additionalFeesQuery = supabase
            .from('additional_class_fees')
            .select('*')
            .eq('school_id', student.school_id)
            .eq('class_id', student.class_id)
            .eq('is_active', true);
          if (selectedBranchId != null) {
            additionalFeesQuery = additionalFeesQuery.eq('branch_id', selectedBranchId);
          }
          const { data: additionalFeesData } = await additionalFeesQuery;
          
          const monthlyAdditionalFees = additionalFeesData
            ?.filter(fee => fee.period === 'monthly')
            .reduce((sum, fee) => sum + (parseFloat(fee.amount) || 0), 0) || 0;
          
          const oneTimeAdditionalFees = additionalFeesData
            ?.filter(fee => fee.period === 'one_time')
            .reduce((sum, fee) => sum + (parseFloat(fee.amount) || 0), 0) || 0;

          // Fetch academic year
          const { data: academicYearData } = await supabase
            .from('academic_years')
            .select('*')
            .eq('school_id', student.school_id)
            .eq('is_active', true)
            .single();

          let academicYearStart, academicYearEnd;
          
          if (academicYearData) {
            academicYearStart = new Date(academicYearData.start_date);
            academicYearEnd = new Date(academicYearData.end_date);
          } else {
            if (classFeesData.first_fee_date) {
              const firstFeeDate = new Date(classFeesData.first_fee_date);
              academicYearStart = new Date(firstFeeDate.getFullYear(), firstFeeDate.getMonth(), 1);
              academicYearEnd = new Date(firstFeeDate.getFullYear() + 1, firstFeeDate.getMonth() - 1, 0);
            } else {
              const today = new Date();
              const currentYear = today.getFullYear();
              const currentMonth = today.getMonth();
              
              if (currentMonth >= 7) {
                academicYearStart = new Date(currentYear, 7, 1);
                academicYearEnd = new Date(currentYear + 1, 6, 31);
              } else {
                academicYearStart = new Date(currentYear - 1, 7, 1);
                academicYearEnd = new Date(currentYear, 6, 31);
              }
            }
          }
          
          const monthlyFees = [];
          let currentDate = new Date(academicYearStart);
          let isFirstMonth = true;
          
          while (currentDate <= academicYearEnd) {
            const monthStart = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
            const monthEnd = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
            
            const scheduledFee = feeScheduleData?.find(fee => {
              const feeDate = new Date(fee.fee_date);
              return feeDate.getMonth() === currentDate.getMonth() && 
                     feeDate.getFullYear() === currentDate.getFullYear();
            });

            let periodStart = monthStart;
            let periodEnd = monthEnd;
            
            if (classFeesData.first_fee_date) {
              const firstFeeDate = new Date(classFeesData.first_fee_date);
              const expiryDuration = classFeesData.expiry_duration || 30;
              
              if (currentDate.getMonth() === firstFeeDate.getMonth() && 
                  currentDate.getFullYear() === firstFeeDate.getFullYear()) {
                periodStart = new Date(firstFeeDate);
                periodEnd = new Date(firstFeeDate);
                periodEnd.setDate(periodEnd.getDate() + expiryDuration - 1);
              } else {
                const monthsFromStart = (currentDate.getFullYear() - firstFeeDate.getFullYear()) * 12 + 
                                       (currentDate.getMonth() - firstFeeDate.getMonth());
                
                switch (classFeesData.fee_type) {
                  case 'monthly':
                    periodStart = new Date(firstFeeDate);
                    periodStart.setMonth(periodStart.getMonth() + monthsFromStart);
                    periodEnd = new Date(periodStart);
                    periodEnd.setDate(periodEnd.getDate() + expiryDuration - 1);
                    break;
                  case 'quarterly':
                    periodStart = new Date(firstFeeDate);
                    periodStart.setMonth(periodStart.getMonth() + (monthsFromStart * 3));
                    periodEnd = new Date(periodStart);
                    periodEnd.setDate(periodEnd.getDate() + expiryDuration - 1);
                    break;
                  case 'semester':
                    periodStart = new Date(firstFeeDate);
                    periodStart.setMonth(periodStart.getMonth() + (monthsFromStart * 6));
                    periodEnd = new Date(periodStart);
                    periodEnd.setDate(periodEnd.getDate() + expiryDuration - 1);
                    break;
                  case 'annual':
                    if (monthsFromStart === 0) {
                      periodStart = new Date(firstFeeDate);
                      periodEnd = new Date(firstFeeDate);
                      periodEnd.setDate(periodEnd.getDate() + expiryDuration - 1);
                    } else {
                      currentDate.setMonth(currentDate.getMonth() + 1);
                      continue;
                    }
                    break;
                }
              }
            }

            const baseFeeAmount = scheduledFee ? scheduledFee.amount : classFeesData.amount;
            const oneTimeFee = isFirstMonth ? oneTimeAdditionalFees : 0;
            const finalFeeAmount = baseFeeAmount + monthlyAdditionalFees + oneTimeFee;
            
            const finalCurrency = scheduledFee ? scheduledFee.currency : classFeesData.currency || 'PKR';
            const expiryDuration = classFeesData.expiry_duration || 30;
            const finalDueDate = new Date(periodStart);
            finalDueDate.setDate(finalDueDate.getDate() + expiryDuration);

            const periodStartStr = periodStart.toISOString().split('T')[0];
            const periodEndStr = periodEnd.toISOString().split('T')[0];
            
            if (todayStr >= periodStartStr && todayStr <= periodEndStr) {
              monthlyFees.push({
                month: currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' }),
                periodStart: periodStart,
                periodEnd: periodEnd,
                dueDate: finalDueDate,
                feeAmount: finalFeeAmount,
                baseFeeAmount: baseFeeAmount,
                additionalFees: monthlyAdditionalFees + oneTimeFee,
                currency: finalCurrency,
                feeType: classFeesData.fee_type || 'monthly',
                isScheduled: !!scheduledFee,
                description: scheduledFee?.description || '',
                isDueNow: true,
                isPaid: false
              });
            }

            if (isFirstMonth) {
              isFirstMonth = false;
            }

            currentDate.setMonth(currentDate.getMonth() + 1);
          }

          // Fetch payment status from fee_records
          if (monthlyFees.length > 0) {
            const feePeriods = monthlyFees.map(fee => {
              const monthParts = fee.month ? fee.month.split(' ') : ['', ''];
              const monthName = monthParts[0] || '';
              const year = monthParts[1] || new Date().getFullYear().toString();
              const monthMap = {
                'January': '01', 'February': '02', 'March': '03', 'April': '04',
                'May': '05', 'June': '06', 'July': '07', 'August': '08',
                'September': '09', 'October': '10', 'November': '11', 'December': '12'
              };
              const monthNum = monthMap[monthName] || String(new Date().getMonth() + 1).padStart(2, '0');
              return `${year}-${monthNum}`;
            });
            
            let feeRecordsQuery = supabase
              .from('fee_records')
              .select('*')
              .eq('school_id', student.school_id)
              .eq('class_id', student.class_id)
              .eq('section_id', student.section_id)
              .eq('student_id', student.id)
              .in('fee_period', feePeriods);
            if (selectedBranchId != null) {
              feeRecordsQuery = feeRecordsQuery.eq('branch_id', selectedBranchId);
            }
            const { data: feeRecordsData } = await feeRecordsQuery;
            
            const paymentStatusMap = {};
            if (feeRecordsData) {
              feeRecordsData.forEach(record => {
                paymentStatusMap[record.fee_period] = record.is_paid;
              });
            }
            
            monthlyFees.forEach(fee => {
              const monthParts = fee.month ? fee.month.split(' ') : ['', ''];
              const monthName = monthParts[0] || '';
              const year = monthParts[1] || new Date().getFullYear().toString();
              const monthMap = {
                'January': '01', 'February': '02', 'March': '03', 'April': '04',
                'May': '05', 'June': '06', 'July': '07', 'August': '08',
                'September': '09', 'October': '10', 'November': '11', 'December': '12'
              };
              const monthNum = monthMap[monthName] || String(new Date().getMonth() + 1).padStart(2, '0');
              const feePeriod = `${year}-${monthNum}`;
              fee.isPaid = paymentStatusMap[feePeriod] || false;
            });
          }

          feesByStudent[student.id] = monthlyFees;
        } catch (err) {
          console.error(`Error fetching fees for student ${student.id}:`, err);
        }
      }
      
      setStudentsFees(feesByStudent);
    } catch (err) {
      console.error('Error fetching students fees:', err);
      setStudentsFees({});
    } finally {
      setLoadingFees(false);
    }
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <div className="school-dashboard">
      <SchoolMobileHeader 
        schoolData={schoolData} 
        mobileMenuOpen={mobileMenuOpen} 
        setMobileMenuOpen={setMobileMenuOpen} 
        handleLogout={handleLogout} 
      />
      
      {/* Sidebar */}
      <SchoolSidebar schoolData={schoolData} onLogout={handleLogout} />
      <div className="dashboard-content student-fees-content-wrapper">
        <div className="student-fees-page-container">
          {/* Page Header Card */}
          <div className="student-fees-page-header">
            <span className="student-fees-header-icon">💰</span>
            <h1 className="student-fees-page-title">Fee Management</h1>
            <p className="student-fees-page-subtitle">View fee information for students by class</p>
          </div>

        <div className="branch-filter-row">
          <BranchSelector
            schoolId={schoolData?.id}
            selectedBranchId={selectedBranchId}
            onBranchChange={handleBranchChange}
          />
          <span className="branch-context-text">
            {selectedBranchId
              ? 'Filtering student fees by the selected branch'
              : 'Showing student fees for all branches'}
          </span>
        </div>

          {/* Filter Section Card */}
          <div className="student-fees-filters-card">
            <div className="filter-group">
              <label htmlFor="class-select" className="filter-label">SELECT CLASS:</label>
              <select
                id="class-select"
                value={selectedClassId}
                onChange={(e) => {
                  setSelectedClassId(e.target.value);
                  setSelectedSectionId('');
                }}
                className="filter-select"
              >
                <option value="">-- Select Class --</option>
                {classes.map(classItem => (
                  <option key={classItem.id} value={classItem.id}>
                    {classItem.class_name}
                  </option>
                ))}
              </select>
            </div>

            {selectedClassId && (
              <div className="filter-group">
                <label htmlFor="section-select" className="filter-label">SELECT SECTION:</label>
                <select
                  id="section-select"
                  value={selectedSectionId}
                  onChange={(e) => setSelectedSectionId(e.target.value)}
                  className="filter-select"
                  disabled={loadingSections}
                >
                  <option value="">-- All Sections --</option>
                  {sections.map((section, index) => (
                    <option key={index} value={section.section_name}>
                      {section.section_name}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>

          {/* Students List */}
          {loadingStudents ? (
            <div className="student-fees-loading">
              <div className="loading-spinner">⏳</div>
              <p>Loading students...</p>
            </div>
          ) : students.length === 0 ? (
            <div className="student-fees-empty-state">
              <div className="empty-state-icon">📋</div>
              <p className="empty-state-message">No students found</p>
            </div>
          ) : (
            <div className="student-fees-table-container">
              <table className="student-fees-table">
                <thead>
                  <tr>
                    <th className="student-name-column">Student Name</th>
                    <th className="fee-amount-column">Fee Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {students.map(student => {
                    const studentFees = studentsFees[student.id] || [];
                    const studentName = `${student.first_name} ${student.middle_name ? student.middle_name + ' ' : ''}${student.last_name}`;
                    const totalDue = studentFees.filter(f => !f.isPaid).reduce((sum, f) => sum + f.feeAmount, 0);
                    const totalPaid = studentFees.filter(f => f.isPaid).reduce((sum, f) => sum + f.feeAmount, 0);
                    const totalAmount = totalDue + totalPaid;
                    
                    return (
                      <tr key={student.id} className="student-fee-row">
                        <td className="student-name-cell">
                          <div className="student-name-wrapper">
                            <div className="student-name-main">{studentName}</div>
                            <div className="student-meta-info">
                              ID: {student.student_id_no || student.email || 'N/A'} | Section: {student.section_id || 'N/A'}
                            </div>
                          </div>
                        </td>
                        <td className="fee-amount-cell">
                          <div className="fee-amount-wrapper">
                            <div className="fee-amount-main">{totalAmount.toLocaleString()} PKR</div>
                            <div className="fee-breakdown">
                              <span className="fee-paid">Paid: {totalPaid.toLocaleString()} PKR</span>
                              <span className="fee-due">Due: {totalDue.toLocaleString()} PKR</span>
                            </div>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default StudentFees;

